import { useMemo, useState } from 'react';
import { ActionStatusType } from '../../store/inspection/observation/actionTypeStore'; // adjust path as needed
import { ObservationModel } from '../../data/types';
import { Clock } from 'lucide-react';
import ColorsHelper from '../../helpers/colours-helpers';
import WearDisplayHelpers from '../../helpers/wearDisplayHelpers';
import InspectionStatusHistory from '../organisms/InspectionStatusHistory';
import { useInspectionCached } from '../../hooks/useInspectionCached';
import { ObservableLevelEnumValue } from '../../data/enums';
import { ErrorBanner } from '../atoms/ErrorBanner';
import { useActionStatusOptimized } from '../../hooks/useActionStatusOptimized';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

interface ActionStatusDisplayProps {
  observations: ObservationModel[];
  paths: string[];
  pathsLike: string[];
  entities: string[];
  dataLevel: ObservableLevelEnumValue;
  showPercentage?: boolean;
  title?: string | 'Action Status';
  useSuffixIcon?: boolean;
  isDisplayingMeasurements?: boolean;
}

export const ActionStatusDisplay = ({
  observations,
  showPercentage = true,
  title = 'Action Status',
  useSuffixIcon = true,
  isDisplayingMeasurements,
  paths,
  pathsLike,
  entities,
  dataLevel,
}: ActionStatusDisplayProps) => {
  const [showHistory, setShowHistory] = useState(false);

  // Use optimized action status hook to prevent constant recalculation
  // Always call the hook to maintain hooks consistency (fixes hooks rule violation)
  const actionStatusQuery = useActionStatusOptimized(observations);

  const actionStatus = {
    isLoading: actionStatusQuery.isLoading,
    isError: actionStatusQuery.isError,
    error: actionStatusQuery.error,
    data: actionStatusQuery.data,
  };

  if (actionStatus.isLoading) {
    // Only ever shown before the FIRST status computes (keepPreviousData holds the
    // previous status across value changes - no spinner flash mid-capture). Shaped
    // like the real bar so the content lands without a layout shift.
    return (
      <div
        className="mb-6 flex items-center justify-center rounded-lg bg-brandLight/30 p-4"
        aria-busy="true"
      >
        <Skeleton width={220} height={24} />
      </div>
    );
  }

  if (actionStatus.isError) {
    return (
      <div>
        <h2 className="mb-4 text-lg font-bold">{title}</h2>
        <div className="rounded-lg border p-4">
          {actionStatus.error instanceof Error
            ? actionStatus.error.message
            : 'An error occurred'}
        </div>
      </div>
    );
  }

  if (!actionStatus.data) {
    return (
      <div>
        <h2 className="mb-4 text-lg font-bold">{title}</h2>
        <div className="rounded-lg border p-4">
          No action status or condition results available.
        </div>
      </div>
    );
  }

  const status = actionStatus.data;

  const isPercentageBelowZero = status.percentage < 0;

  const isNoMeasurement =
    isDisplayingMeasurements &&
    isPercentageBelowZero &&
    status.actionType?.name
      ?.toLowerCase()
      .includes(ActionStatusType.noMeasurements.toLowerCase());

  const validMeasurement =
    status.measurement !== undefined && status.measurement >= 0;

  return (
    <div className="items-center justify-between space-y-6">
      {/* Action Status Section */}
      <div className="mb-6 rounded-lg bg-brandLight/30 p-4">
        <div className="flex items-center justify-center space-x-2">
          <div className="flex items-center space-x-4">
            <span>{title}:</span>
            {showPercentage && !isPercentageBelowZero && (
              <span>
                {`${WearDisplayHelpers.formatActionStatusPercentage(status.percentage)}%`}
                {validMeasurement
                  ? ` (${WearDisplayHelpers.formatActionStatusWear(status.measurement!)}mm)`
                  : ''}
              </span>
            )}
            <span
              className="rounded-full px-5 py-1 text-sm"
              style={{
                backgroundColor: isNoMeasurement
                  ? ColorsHelper.ACTION_STATUS_BAR_COLORS.NoMeasurements
                  : status.actionType?.defaultColourCode,
                color: ColorsHelper.getContrastColor(
                  status.actionType?.defaultColourCode,
                ),
              }}
            >
              {isNoMeasurement
                ? 'No Measurement'
                : status.actionType?.description}
            </span>
          </div>
          {useSuffixIcon && (
            <Clock
              className="size-5 cursor-pointer text-ink"
              onClick={() => setShowHistory(true)}
            />
          )}
        </div>
      </div>

      <InspectionStatusHistory
        idenifiers={paths}
        pathsLike={pathsLike}
        title="Status History"
        onClose={() => setShowHistory(false)}
        isOpen={showHistory}
        entities={entities}
        dataLevel={dataLevel}
      />
    </div>
  );
};

interface ActionStatusBarProps {
  inspectionId: string;
  title?: string;
  inspectionCategoryTypeId: string;
  paths: string[];
  pathsLike: string[];
  dataLevel: ObservableLevelEnumValue;
  entities: ActionStatusEntity[];
  useSuffixIcon?: boolean;
  showPercentage?: boolean;
}

interface ActionStatusEntity {
  entityId: number;
  entityExternalId: string;
}

export const ActionStatusBar = ({
  inspectionId,
  title = 'Action Status',
  inspectionCategoryTypeId,
  paths,
  pathsLike,
  useSuffixIcon = true,
  dataLevel,
  entities,
  showPercentage,
}: ActionStatusBarProps) => {
  // Shared cached query instead of a per-mount useLiveQuery: warm mounts get the
  // inspection synchronously on the first frame (no undefined frame -> no empty
  // observation key -> no status flash), and draft flushes still propagate via the
  // inspection-key invalidation.
  const { inspection: inspectionViewModel } = useInspectionCached(inspectionId);
  const inspection = inspectionViewModel?.inspection;
  // Removed observations state - using memoized filteredObservations directly
  const [error, setError] = useState<string | null>(null);

  const isCategoriesIncludingMeasurements = useMemo(() => {
    const observationsContainsMeasurement =
      paths.some((like) =>
        like.toLowerCase().includes(ActionStatusType.measurement.toLowerCase()),
      ) ||
      pathsLike.some((like) =>
        like.toLowerCase().includes(ActionStatusType.measurement.toLowerCase()),
      );
    return observationsContainsMeasurement;
  }, [paths, pathsLike]);

  // Memoize the observation filtering to prevent unnecessary recalculations
  const filteredObservations = useMemo(() => {
    if (!inspection) return [];

    const inspectionCategoryType = inspection.inspectionCategoryTypes.find(
      (category) => category.externalId === inspectionCategoryTypeId,
    );

    if (!inspectionCategoryType) {
      setError('Inspection category type not found');
      return [];
    }

    setError(null);

    const observations = inspectionCategoryType.observations.filter((obs) => {
      return (
        paths.includes(obs.observableItem.identifier) ||
        pathsLike.some((like) => obs.observableItem.identifier.startsWith(like))
      );
    });

    const entityExternalIds = new Set(entities.map((e) => e.entityExternalId));
    return observations.filter(
      (obs) =>
        obs.dataLevelId === dataLevel &&
        entityExternalIds.has(obs.entityExternalId),
    );
  }, [
    inspection,
    inspectionCategoryTypeId,
    paths,
    pathsLike,
    dataLevel,
    entities,
  ]);

  // No need to update observations atom - ActionStatusDisplay handles this internally

  if (error) {
    return <ErrorBanner message={error} />;
  }

  return (
    <ActionStatusDisplay
      observations={filteredObservations}
      showPercentage={showPercentage}
      useSuffixIcon={useSuffixIcon}
      isDisplayingMeasurements={isCategoriesIncludingMeasurements}
      title={title}
      paths={paths}
      pathsLike={pathsLike}
      entities={entities.map((e) => e.entityExternalId)}
      dataLevel={dataLevel}
    />
  );
};
